/**
 * The comparison with Google's results, held back because those results are about something else
 * (TECHNICAL_DEBT §0ar).
 *
 * When a page's results are about another subject — the Linear app for a post on linear project
 * management — every card built from them is measured against the wrong pages: missing sections,
 * freshness, answer structure (with its vocabulary) and page type. The server scores the results
 * against the page and says `mismatch`; this turns that into the one sentence the belt, the agent
 * export and the MCP all show, so the three cannot word it differently.
 *
 * Two things the sentence must do, both from the first graded walk:
 * - **Show the evidence.** "About something else" cannot be checked by the reader, and for "agency
 *   sop templates" it even sounds on topic. So it quotes the top results' own titles, as stored.
 *   No model labels the topic: that would be a second, unchecked claim.
 * - **Name only what was held back on this page**, in the words the cards use. A page that only
 *   lost its missing sections must not read as if it lost four kinds of advice.
 *
 * It must not read like "nothing found" (memory: confusable states need different copy).
 *
 * **It is also the page's intent verdict** (quality sprint 2, the owner's review of the first
 * walk): "held back" alone gives the writer nothing to do. So, from evidence the server read
 * (`PageSerpSnapshot.heldBackIntent`, no model call), the same sentence says
 * 1. what searchers want: a brand's search (the #1 result's own domain is one of the search's
 *    words), results about something else, or results on the page's subject that ask for another
 *    task on it (`subjectMatch.reason` `task`, quality sprint 3), quoting them as before;
 * 2. whether to compete: never for a brand's search; otherwise re-target the page when there is a
 *    target, and say the search is not worth chasing with this page when there is none;
 * 3. what to aim at instead: the page's other Search Console searches, or, without Search Console,
 *    the searches we have it ranking for. With neither it says there is nothing on record, rather
 *    than guess. Only a search on the page's own subject is suggested (quality sprint 5 E: the
 *    server drops one that names none of the page's subject words); when the source has searches
 *    but none on the subject, it says that, naming the subject, and suggests none.
 * The export's "Held back" line, the MCP's `heldBack` and the page brief all read this one `text`,
 * so they cannot word it differently.
 *
 * **When the page already has a search, the fault is ours** (quality sprint 4, G; the owner's review
 * of sprint 3: a converter compared on embedding a video, a remover on adding protection). When the
 * first search to aim at is one the page already ranks on page one for, or its Search Console top
 * search (`heldBackIntent.ownSearchFirst`, decided on the server), the held-back search was our pick,
 * not the page's. The card then says we compared the wrong search and that the page is not at fault,
 * never "Re-target this page" (badge WRONG SEARCH). A brand's search keeps its own verdict.
 *
 * The belt card shows the same verdict as `card` (the owner's review of the final walk: the
 * paragraph was too long to read at a glance). One short verdict line, then the results and the
 * searches to aim at as lists, the way the Page type card shows its examples. `card` is built from
 * the same pieces as `text`, never worded separately.
 */

import type { PageSerpResultRow, PageSerpSnapshot, SerpHeldBackIntent, SerpHeldBackInstead } from './api-types';

/** What the page would have shown, had the comparison not been held back. */
export type SerpSubjectWithheldKind =
  | 'missing_sections'
  | 'thin_sections'
  | 'freshness'
  | 'answer_structure'
  | 'page_type';

/**
 * What searchers want, in three states the card must never word alike: a brand's search (do not
 * compete), results about another subject (re-target), and results on the page's subject that ask
 * for the opposite or a different task on it (`subjectMatch.reason` `task`: "make it read-only" for
 * a tool that removes read-only). The last reads as "a different task", never "something else": the
 * results visibly share the page's words, and "something else" would read as false.
 */
export type SerpSubjectWithheldVerdict = 'brand' | 'other_subject' | 'other_task';

export interface SerpSubjectWithheldNotice {
  /** The search whose results were about something else, as the page header shows it. */
  query: string;
  /**
   * What was held back on this page, in card order. Empty when no card built from these results
   * would have shown anything: the verdict still stands, and the list is left out rather than
   * invented, so a held-back page never reads as one with nothing to fix.
   */
  kinds: SerpSubjectWithheldKind[];
  /** The top results quoted as evidence, as stored. */
  evidence: SerpSubjectWithheldResult[];
  verdict: SerpSubjectWithheldVerdict;
  /** The brand the search belongs to, when `verdict` is `brand`. */
  brand: SerpHeldBackIntent['brand'];
  /** Searches to aim at instead, or null when nothing is on record. */
  instead: SerpHeldBackInstead | null;
  /**
   * The card's badge: BRAND SEARCH, WRONG SEARCH (the page already has its own search), RE-TARGET
   * (with a target), or OTHER SUBJECT / OTHER TASK (without).
   */
  badge: string;
  /** The whole verdict, for the export's held-back list, the MCP and the page brief. One line. */
  text: string;
  /** The same verdict laid out for the belt card. */
  card: SerpSubjectWithheldCard;
}

export interface SerpSubjectWithheldResult {
  position: number;
  domain: string;
  title: string;
}

/** The belt card: what to do in one line, the evidence and the targets as lists. */
export interface SerpSubjectWithheldCard {
  verdictLine: string;
  /** Searches to aim at, with the label naming their source. Null when nothing is on record. */
  instead: { label: string; rows: Array<{ query: string; detail: string }> } | null;
  /** Said instead of the list when the server checked and found nothing; null otherwise. */
  nothingLine: string | null;
  /** What was held back, by the cards' own short names; null when nothing was. */
  heldLine: string | null;
}

/**
 * How many result titles are quoted. Three is enough to show what the results are about and still
 * fits one line on the belt; the score itself reads the top eight.
 */
export const SERP_SUBJECT_WITHHELD_EVIDENCE_COUNT = 3;

/** A quoted title longer than this is cut at a word. Titles past it are mostly a site's tagline. */
export const SERP_SUBJECT_WITHHELD_TITLE_MAX_CHARS = 70;

/** Plain words, after the cards' own names ("Missing sections", "Last update", "Page type"). */
const KIND_PHRASE: Record<SerpSubjectWithheldKind, string> = {
  missing_sections: 'the missing sections',
  thin_sections: 'the thin sections',
  freshness: 'the advice to update the page',
  answer_structure: "the advice on the page's opening and wording",
  page_type: 'the page type advice'
};

/** The short names for the belt card's held-back line. */
const KIND_SHORT: Record<SerpSubjectWithheldKind, string> = {
  missing_sections: 'missing sections',
  thin_sections: 'thin sections',
  freshness: 'update advice',
  answer_structure: 'opening and wording advice',
  page_type: 'page type'
};

/**
 * Three badges for three states. "Re-target" only when there is a target: with nothing on record it
 * would send the writer looking for one we never named (the first grading of this card).
 */
const BADGE_BRAND = 'BRAND SEARCH';
const BADGE_RETARGET = 'RE-TARGET';
const BADGE_OTHER_SUBJECT = 'OTHER SUBJECT';
const BADGE_OTHER_TASK = 'OTHER TASK';
const BADGE_WRONG_SEARCH = 'WRONG SEARCH';

function listOf(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  // Serial comma: the phrases themselves can hold an "and".
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

function cutTitle(title: string): string {
  const t = title.replace(/\s+/g, ' ').trim();
  if (t.length <= SERP_SUBJECT_WITHHELD_TITLE_MAX_CHARS) return t;
  const cut = t.slice(0, SERP_SUBJECT_WITHHELD_TITLE_MAX_CHARS);
  const space = cut.lastIndexOf(' ');
  return `${(space > 30 ? cut.slice(0, space) : cut).replace(/[\s,.:;|–—-]+$/, '')}…`;
}

function topResults(rows: PageSerpResultRow[] | null | undefined): SerpSubjectWithheldResult[] {
  return [...(rows || [])]
    .filter((r) => r && r.position >= 1 && String(r.title || '').trim())
    .sort((a, b) => a.position - b.position)
    .slice(0, SERP_SUBJECT_WITHHELD_EVIDENCE_COUNT)
    .map((r) => ({
      position: r.position,
      domain: String(r.domain || '').replace(/^www\./, ''),
      title: cutTitle(String(r.title))
    }));
}

const capitalise = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const count = (n: number) => Math.round(n).toLocaleString('en-US');
/** Search Console's average position is a mean, never a rank; rounded, and never shown as 0. */
const place = (p: number) => String(Math.max(1, Math.round(p)));

/** Nothing to aim at: which source was checked, said as the end of a sentence. */
function nothingOnRecord(intent: SerpHeldBackIntent): string {
  return intent.insteadSource === 'search_console'
    ? 'Search Console shows no other search for it often enough to suggest one'
    : 'there is no other search on record for it yet';
}

/**
 * Searches on record, none on the page's own subject (`insteadOffSubject`, quality sprint 5 E): one
 * whole sentence. It names the subject word so the reader can check it against the title, and says
 * we suggest none, so it reads neither as "nothing on record" (we looked and found searches) nor as
 * "nothing to fix" (the comparison is still held back). Null when the state does not apply.
 */
function offSubjectSentence(intent: SerpHeldBackIntent | null | undefined): string | null {
  if (!intent || intent.instead || !Array.isArray(intent.insteadOffSubject)) return null;
  const terms = intent.insteadOffSubject.map((w) => `“${w}”`);
  const subject = terms.length
    ? `${terms.length === 1 ? terms[0] : `${terms.slice(0, -1).join(', ')} or ${terms[terms.length - 1]}`}, this page's own subject`
    : "this page's own subject";
  return intent.insteadSource === 'search_console'
    ? `Search Console shows it for other searches, but none of them is about ${subject}, so we suggest none.`
    : `It ranks for other searches by our estimate, but none of them is about ${subject}, so we suggest none.`;
}

/** The page already has a search: the held-back one was our pick, not the page's. */
function ownSearch(intent: SerpHeldBackIntent | null | undefined): boolean {
  return !!intent?.ownSearchFirst && !intent.brand && !!intent.instead?.queries.length;
}

/**
 * Step 3: what to aim at instead. Empty when there is nothing; step 2 then says so. `own` words the
 * list as the page's searches rather than targets (the page is not at fault).
 */
function insteadSentence(intent: SerpHeldBackIntent | null | undefined, own = ownSearch(intent)): string {
  if (!intent) return '';
  const instead = intent.instead;
  if (instead?.source === 'search_console') {
    const qs = instead.queries.map(
      (q) => `“${q.query}” (${count(q.impressions)} impressions, average position ${place(q.position)})`
    );
    return own
      ? `Searches Search Console showed it for in the last ${instead.windowDays} days: ${listOf(qs)}.`
      : `Aim it instead at searches Search Console showed it for in the last ${instead.windowDays} days: ${listOf(qs)}.`;
  }
  if (instead?.source === 'ranked_keywords') {
    const qs = instead.queries.map((q) =>
      q.searchVolume !== null
        ? `“${q.query}” (position ${place(q.position)}, about ${count(q.searchVolume)} searches a month)`
        : `“${q.query}” (position ${place(q.position)})`
    );
    return own
      ? `Searches it already ranks for, by our estimate (not Search Console): ${listOf(qs)}.`
      : `Aim it instead at searches it already ranks for, by our estimate (not Search Console): ${listOf(qs)}.`;
  }
  return '';
}

/** The belt card's list of searches to aim at, from the same evidence as `insteadSentence`. */
function insteadRows(intent: SerpHeldBackIntent | null | undefined): SerpSubjectWithheldCard['instead'] {
  const instead = intent?.instead;
  const own = ownSearch(intent);
  if (instead?.source === 'search_console') {
    return {
      label: own
        ? `Its searches · Search Console, last ${instead.windowDays} days`
        : `Aim it instead at · Search Console, last ${instead.windowDays} days`,
      rows: instead.queries.map((q) => ({
        query: q.query,
        detail: `${count(q.impressions)} impressions · avg. position ${place(q.position)}`
      }))
    };
  }
  if (instead?.source === 'ranked_keywords') {
    return {
      label: own
        ? 'Searches it already ranks for · our estimate, not Search Console'
        : 'Aim it instead at · our estimate, not Search Console',
      rows: instead.queries.map((q) => ({
        query: q.query,
        detail:
          q.searchVolume !== null
            ? `position ${place(q.position)} · ${count(q.searchVolume)} searches a month`
            : `position ${place(q.position)}`
      }))
    };
  }
  return null;
}

/**
 * The notice, or null when the results match the page, were never scored, or nothing on this page
 * was built from them (then there is nothing held back to explain).
 *
 * A payload without `heldBackIntent` (one the server did not enrich) reads as another subject not
 * worth chasing, and makes no claim about what is on record.
 */
export function serpSubjectWithheldNotice(
  pageSerp: Pick<PageSerpSnapshot, 'query' | 'queryDisplay' | 'results' | 'subjectMatch' | 'heldBackIntent'> | null | undefined,
  kinds: SerpSubjectWithheldKind[]
): SerpSubjectWithheldNotice | null {
  if (!pageSerp?.subjectMatch?.mismatch) return null;
  const query = String(pageSerp.queryDisplay || pageSerp.query || '').trim();
  const evidence = topResults(pageSerp.results);
  const brand = pageSerp.heldBackIntent?.brand ?? null;
  const instead = pageSerp.heldBackIntent?.instead ?? null;
  const verdict: SerpSubjectWithheldVerdict = brand
    ? 'brand'
    : pageSerp.subjectMatch.reason === 'task'
      ? 'other_task'
      : 'other_subject';
  const about = verdict === 'other_task' ? "are about a different task from this page's" : 'are about something else';
  const quotedSearch = query ? `“${query}”` : "this page's search";
  const quoted = evidence.map((e) => (e.domain ? `${e.domain}: “${e.title}”` : `“${e.title}”`));
  const tops = quoted.length ? `the top results are ${listOf(quoted)}` : '';

  const intentEvidence = pageSerp.heldBackIntent ?? null;
  const nothing = !!intentEvidence && !instead;
  const offSubject = offSubjectSentence(intentEvidence);
  // What the card says when it suggests nothing: searches on record but none on the subject, or none on record.
  const nothingSentence = !nothing ? '' : offSubject ?? `${capitalise(nothingOnRecord(intentEvidence!))}.`;
  const own = ownSearch(intentEvidence);
  const first = instead?.queries[0];
  // Where the page already stands on its own search, said the way the source can say it.
  const ownStanding = !own || !first
    ? ''
    : instead!.source === 'ranked_keywords'
      ? `it already ranks #${place(first.position)} for “${first.query}”`
      : `its top search in Search Console is “${first.query}”`;

  // Steps 1 and 2: what searchers want, and whether to compete for it. Another subject with a
  // target is "re-target"; with none it is "not worth chasing", never a re-target with no target.
  const intent = brand
    ? `Google reads ${quotedSearch} as a search for ${brand.name}: ${brand.domain} ranks first${tops ? `, and ${tops}` : ''}. ` +
      `The search belongs to that brand, so this page should not compete for it.` +
      (nothing ? ` ${nothingSentence}` : '')
    : `Google's results for ${quotedSearch} ${about}${tops ? `: ${tops}` : ''}. ` +
      (own
        ? `We compared this page on the wrong search: ${ownStanding}. The page is not at fault, and there is nothing to change on it for ${quotedSearch}.`
        : instead
        ? `Re-target this page rather than compete for this search.`
        : nothing
          ? offSubject
            ? `This search is not worth chasing with this page. ${offSubject}`
            : `This search is not worth chasing with this page, and ${nothingOnRecord(intentEvidence!)}.`
          : `This search is not worth chasing with this page.`);
  const verdictLine = brand
    ? `Google reads ${quotedSearch} as a search for ${brand.name}. This page should not compete for it.`
    : own
      ? `We compared this page on the wrong search. ${capitalise(ownStanding)}.`
      : `Google's results for ${quotedSearch} ${about}. ` +
        (instead ? 'Re-target this page.' : 'Not worth chasing with this page.');
  const held = kinds.length
    ? `We held back ${listOf(kinds.map((k) => KIND_PHRASE[k]))}, which ${kinds.length === 1 ? 'was' : 'were'} measured against these results.`
    : '';

  return {
    query,
    kinds,
    evidence,
    verdict,
    brand,
    instead,
    badge: brand
      ? BADGE_BRAND
      : own
        ? BADGE_WRONG_SEARCH
        : instead
        ? BADGE_RETARGET
        : verdict === 'other_task'
          ? BADGE_OTHER_TASK
          : BADGE_OTHER_SUBJECT,
    text: [intent, insteadSentence(pageSerp.heldBackIntent), held].filter(Boolean).join(' '),
    card: {
      verdictLine,
      instead: insteadRows(pageSerp.heldBackIntent),
      nothingLine: nothing ? nothingSentence : null,
      heldLine: kinds.length ? `Held back: ${listOf(kinds.map((k) => KIND_SHORT[k]))}.` : null
    }
  };
}

/**
 * The results show another page of this site, not this one (quality sprint 7 C).
 *
 * Several pages can be compared on one search, and the stored results then match a page only at
 * the domain (`matchType` `domain`): the position and the URL are another page's. Judged on them, the
 * page inherited that page's rank ("moved to position 4"), rival sections from a search Google gives
 * to the other page, and the page-level verdicts. So the review surface holds the comparison back
 * through the same gate as results about something else, and says what is true instead, once.
 */
export function serpResultsShowAnotherPage(
  pageSerp: { matchType?: string | null; yourUrl?: string | null } | null | undefined
): boolean {
  return pageSerp?.matchType === 'domain' && !!String(pageSerp.yourUrl || '').trim();
}

/**
 * What the notice concludes, three states worded apart:
 * - `competing_page`: the engine says the two pages compete. The pair's Cannibalization card says
 *   what to do; this only points there, so the advice is given once.
 * - `wrong_search`: they do not compete, so the fault is our pick of the comparison search, not the
 *   page (the held-back card's WRONG SEARCH reading, §0ci amendment of 2026-10-18). Nothing for the
 *   writer to do: no card on `/review`, the everything export only (owner, 2026-10-19).
 * - `unchecked`: the engine was not read for this payload; the notice states the fact and no verdict.
 */
export type SerpOtherPageVerdict = 'competing_page' | 'wrong_search' | 'unchecked';

export interface SerpOtherPageNotice {
  query: string;
  /** The other page, as Google lists it, and its path as the notice names it. */
  otherUrl: string;
  otherPath: string;
  /** Where the other page sits in these results. */
  otherPosition: number | null;
  verdict: SerpOtherPageVerdict;
  kinds: SerpSubjectWithheldKind[];
  /** The page's own searches, when the server read them (`heldBackIntent`); null otherwise. */
  instead: SerpHeldBackInstead | null;
  badge: string;
  /** The whole notice, one line, for the everything export. */
  text: string;
}

const BADGE_COMPETING_PAGE = 'COMPETING PAGE';
const BADGE_ANOTHER_PAGE = 'ANOTHER PAGE';

function pathOfUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.pathname || '/'}${u.search}`;
  } catch {
    return url;
  }
}

/** The notice, or null unless these results show another page of the site and not this one. */
export function serpOtherPageNotice(
  pageSerp:
    | (Pick<PageSerpSnapshot, 'query' | 'queryDisplay' | 'matchType' | 'yourUrl' | 'yourPosition' | 'heldBackIntent' | 'shownPageCompetes' | 'searchConsoleHolder'> & {
        fetchedAt?: string | null;
      })
    | null
    | undefined,
  kinds: SerpSubjectWithheldKind[]
): SerpOtherPageNotice | null {
  if (!pageSerp || !serpResultsShowAnotherPage(pageSerp)) return null;
  const query = String(pageSerp.queryDisplay || pageSerp.query || '').trim();
  const otherUrl = String(pageSerp.yourUrl).trim();
  const otherPath = pathOfUrl(otherUrl);
  const position = typeof pageSerp.yourPosition === 'number' && pageSerp.yourPosition >= 1 ? pageSerp.yourPosition : null;
  // Where Search Console is on, its rows say which page holds the search; the one fetch does not
  // (round 2: a fetch put a page at #4 that Search Console shows at about #48 every day, with a third
  // page at about #4). A holder that is this page, or no holder, leaves no "wrong search" to claim.
  const sc = pageSerp.searchConsoleHolder;
  const scHolder = sc?.holder ?? null;
  const scNamesAnother = !!scHolder && !scHolder.isThisPage;
  const competes = pageSerp.shownPageCompetes;
  const verdict: SerpOtherPageVerdict =
    competes === true
      ? 'competing_page'
      : competes === false && (!sc || scNamesAnother)
        ? 'wrong_search'
        : 'unchecked';
  const quotedSearch = query ? `“${query}”` : "this page's search";
  const other = otherPath === '/' ? "this site's home page" : `another page of this site, ${otherPath},`;
  const fetchedOn = String(pageSerp.fetchedAt || '').slice(0, 10);
  const fetched = /^\d{4}-\d{2}-\d{2}$/.test(fetchedOn) ? `, fetched once on ${fetchedOn},` : ', fetched once,';
  const fetchFact = `Google's results for ${quotedSearch}${fetched} show ${other} ${position !== null ? `at #${position}` : 'in them'}, and not this page.`;
  const scPlace = (p: number) => String(Math.max(1, Math.round(p)));
  const scFact = !sc
    ? ''
    : !scHolder
      ? `Search Console shows no page of this site for it in the last ${sc.windowDays} days.`
      : scHolder.isShownPage
        ? `Search Console over the last ${sc.windowDays} days agrees: ${scHolder.path} at about #${scPlace(scHolder.position)}.`
        : scHolder.isThisPage
          ? `Search Console over the last ${sc.windowDays} days shows this page itself most for it, at about #${scPlace(scHolder.position)}.`
          : `Search Console over the last ${sc.windowDays} days shows ${scHolder.path} for it instead, at about #${scPlace(scHolder.position)}.`;
  const fact = [fetchFact, scFact].filter(Boolean).join(' ');

  const intent = pageSerp.heldBackIntent ?? null;
  const instead = intent?.instead ?? null;
  let conclusion: string;
  if (verdict === 'competing_page') {
    conclusion = "The two pages compete for this search: the pair's Cannibalization card says what to do.";
  } else if (verdict === 'wrong_search') {
    const searches = insteadSentence(intent, true);
    // Not "to suggest": nothing is being suggested here, the page's own searches are only listed.
    const nothing = !intent || instead
      ? ''
      : intent.insteadSource === 'search_console'
        ? 'Search Console shows no other search for it with enough impressions to name.'
        : 'There is no other search on record for it yet.';
    const holds = sc
      ? 'Another page of this site holds this search'
      : 'In that fetch Google gave this search to the other page';
    conclusion =
      `${holds}, so we compared this page on the wrong search: the page is not at fault, and there is nothing to change on it for this search.` +
      (searches ? ` ${searches}` : nothing ? ` ${nothing}` : '');
  } else {
    conclusion = 'This page is not judged on these results.';
  }
  // Worded so the verb never has to agree with a phrase ("the missing sections, which was").
  const held = kinds.length
    ? `We held back what was measured against these results: ${listOf(kinds.map((k) => KIND_PHRASE[k]))}.`
    : '';

  return {
    query,
    otherUrl,
    otherPath,
    otherPosition: position,
    verdict,
    kinds,
    instead,
    badge: verdict === 'competing_page' ? BADGE_COMPETING_PAGE : verdict === 'wrong_search' ? BADGE_WRONG_SEARCH : BADGE_ANOTHER_PAGE,
    text: [fact, conclusion, held].filter(Boolean).join(' ')
  };
}
